/* tslint:disable:max-line-length */
import {HttpClient} from '@angular/common/http';
import {Injectable, OnDestroy} from '@angular/core';
import {ConfigService} from '@docma-mm-core/config/config.service';
import {BasedataService} from '@docma-mm-core/shared/basedata.service';
import {AppContainerOptions, AppRightDrawerService, PendingService} from '@mydocma/app-container';
import {Notification, NotificationService} from '@mydocma/component-library/src/lib/notification';
import {Task, TaskFileDownloadService, TaskService, TaskStatusDataTypeEnum, TaskStatusEnum} from '@mydocma/task';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {MmReportTask} from '../mm-report-task';
import {MmReportTypeEnum} from '../mm-report-type.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TaskVisibilityEnum} from '@docma-mm-core/task/task-visibility.enum';
import {ReportingModeEnum} from './faults-reports/common/reporting-mode.enum';
import {Guid} from 'guid-typescript';
import {ExtendedNotificationComponent} from '../../mm-components/app-container/extended-notification/extended-notification.component';
import { Logger } from '@mydocma/common/src/lib/logger';

@UntilDestroy()
@Injectable()
export class MmFaultsReportingService extends BasedataService implements OnDestroy {

  private fileDownloadSubject = new Subject<{task: Task, file: File}>();
  private finishSubject = new Subject<Task>();
  private reportReadySubject = new Subject<MmReportTask>();

  public onReportFileDownloaded = this.fileDownloadSubject.asObservable();
  public onReportTaskFinished = this.finishSubject.asObservable();
  public onReportReady = this.reportReadySubject.asObservable();

  private reportPendingState = new Map<string, BehaviorSubject<boolean>>();
  private registerdReportTasks = new Map<string, MmReportTask>();

  constructor(private http: HttpClient, configService: ConfigService,
              private readonly translateService: TranslateService,
              private readonly taskDownloadService: TaskFileDownloadService,
              private readonly taskService: TaskService,
              private readonly appRightDrawerService: AppRightDrawerService,
              private readonly appContainerOptions: AppContainerOptions,
              private readonly notificationService: NotificationService,
              private readonly pendingService: PendingService,
              private readonly logger: Logger) {
    super(configService);

    taskService.onTaskStarted
      .pipe(untilDestroyed(this))
      .subscribe(task => this.onTaskQueued(task));

    taskService.onTaskChanged
    .pipe(
      untilDestroyed(this),
    )
    .subscribe(task => {
      switch (task?.status?.value) {
        case TaskStatusEnum.ended:
          this.onTaskReady(task);
          break;
        case TaskStatusEnum.canceled:
        case TaskStatusEnum.failed:
          // console.warn('task stopPending (0)', task.id, task);
          this.stopPending(task);
          break;
        default:
          break;
      }
    });

  }

  ngOnDestroy(): void {
  }

  private async onTaskQueued(task: Task) {
    if (task.visibility === TaskVisibilityEnum.hidden) {
      return;
    }
    const pendingSubject = new BehaviorSubject<boolean>(true);

    if (!this.reportPendingState.get(task.id)) {
      // console.warn('task registered ', task.id, task);
      this.pendingService.registerPendingState(pendingSubject);
      this.reportPendingState.set(task.id, pendingSubject);
    }
  }


  private async onTaskReady(task: Task) {
    // console.warn('task onTaskReady', task.id, task);

    if (!(task as any).data) {
      // console.warn('task stopPending (1)', task.id, task);
      this.stopPending(task);
      return;
    }

    const reportTask = this.registerdReportTasks.get(task.id);
    const groupTask = task.groupId ? this.registerdReportTasks.get(task.groupId) : null;
    const registeredTask = reportTask || groupTask;
    if (!registeredTask) {
      // console.warn('task stopPending (2)', task.id, task);
      this.stopPending(task);
      return;
    }

    const taskData = Array.isArray((task as any).data) ? (task as any).data[0] : null;
    if (!taskData?.externalReportId) {
      // console.warn('task stopPending (3)', task.id, task);
      this.stopPending(task);
      return;
    }

    const externalReportId = taskData.externalReportId;
    if (groupTask) {   // && groupTask.reports.hasOwnProperty(externalReportId)
      if (!groupTask.reports) {
        groupTask.reports = {};
      }
      if (!groupTask.reports.hasOwnProperty(externalReportId) || !groupTask.reports[externalReportId]) {
        groupTask.reports[externalReportId] = {File: null, IsNotified: true};
      }
      else {
        groupTask.reports[externalReportId].IsNotified = true;
      }
    }

    if (taskData.type === 'emailBody') {
      if (registeredTask?.reports && registeredTask?.reports.hasOwnProperty(externalReportId)) {
        // remove email-body's - they should not be opened
        delete registeredTask?.reports[externalReportId];
      }
    }

    if (registeredTask && (task.visibility !== TaskVisibilityEnum.hidden || registeredTask?.reportType === MmReportTypeEnum.writingPreview || registeredTask?.reportType === MmReportTypeEnum.writingPreviewEmailBody)) {

      const errorSendingEmail = task.statusLog.some(status => status.data?.type === TaskStatusDataTypeEnum.Error && (status.data?.text === 'Error sending email' || status.data?.text === 'Fehler beim Senden der E-Mail'));

      const file = await this.downloadReport(task, registeredTask?.reportType === MmReportTypeEnum.writingPreviewEmailBody);
      if (!registeredTask?.reports || registeredTask?.reports.hasOwnProperty(undefined) || registeredTask.reports[taskData.externalReportId] == null) {
        registeredTask.reports = Object.fromEntries([taskData.externalReportId].map(x => [x, {File: file, IsNotified: true}]));
      } else {
        registeredTask.reports[taskData.externalReportId].File = file;
        registeredTask.reports[taskData.externalReportId].IsNotified = true;
      }
      this.fileDownloadSubject.next({task, file});

      if (!registeredTask.notificationShown && Object.entries(registeredTask?.reports).length > 0 && Object.entries(registeredTask?.reports).every(([_taskId, _file]) => _file?.File != null)) {
        if (registeredTask?.reportType !== MmReportTypeEnum.writingPreview && registeredTask?.reportType !== MmReportTypeEnum.writingPreviewEmailBody) {
          registeredTask.notificationShown = true;
          const notification = new Notification<any>();
          notification.id = Guid.create().toString();
          notification.data.push({
            title: this.translateService.instant('MM.reporting.successNotification.title'),
            content: this.translateService.instant((Object.keys(registeredTask?.reports).length === 1 ? (errorSendingEmail ? 'MM.reporting.successNotification.singular.contentNotSended' : 'MM.reporting.successNotification.singular.content') : (errorSendingEmail ? 'MM.reporting.successNotification.plural.contentNotSended' : 'MM.reporting.successNotification.plural.content'))),
            iconName: 'done',
            iconClass: 'nt-success',
            buttonText: this.translateService.instant((Object.keys(registeredTask?.reports).length === 1 ? 'MM.reporting.successNotification.singular.open' : 'MM.reporting.successNotification.plural.open')),
            buttonAction: async () => {
              await Promise.all(Object.values(registeredTask?.reports).map(async _report => {
                await this.taskDownloadService.downloadFile(_report.File, {open: true, preventBrowserDownload: true});
              }));
            }
          });
          notification.config = {duration: 1000000, showImmediately: true};

          this.notificationService.notify(ExtendedNotificationComponent, notification);
        }
      }
    }

    if (registeredTask) {
      this.reportReadySubject.next(registeredTask);
    }

    // console.warn('task stopPending (4)', task.id, task);
    this.stopPending(task);
  }

  private stopPending(task: Task) {
    const pendingSubject = this.reportPendingState.get(task.id);
    if (pendingSubject) {
      pendingSubject.next(false);
      // console.warn('task un-registered', task.id, task);
      this.pendingService.removePendingState(pendingSubject);
    }
    const _groupTask = (task.groupId ? this.registerdReportTasks.get(task.groupId) : undefined);
    const _task = (task.id ? this.registerdReportTasks.get(task.id) : undefined);

    const reportTask = (_groupTask ?? _task);
    if (reportTask?.reports && Object.values(reportTask.reports).every(r => r == null || !!r?.IsNotified)) {
      // console.warn('registerdReportTasks.delete', task.id, task);
      this.registerdReportTasks.delete(task.id);
    }
    this.finishSubject.next(task);
  }

  public registerReportTask(reportTask: MmReportTask) {
    if (!reportTask.taskId) {
      return;
    }
    this.registerdReportTasks.set(reportTask.taskId, reportTask);
  }

  public async downloadReport(task: Task, openFile: boolean): Promise<File> {

    try {

      if (!task.statusLog) {
        return null;
      }
      // TODO
      let fileUrl: string;
      task.statusLog.forEach(s => {
        if (s.file?.url) {
          fileUrl = s.file.url;
        }
      });
      if (!fileUrl) {
        return null;
      }

      const isIos = this.isIOSDevice();
      const isPreview = ((task as any).data[0].mode === ReportingModeEnum.Preivew ||
                                  (task as any).data[0].mode === ReportingModeEnum.PreviewSingle);
      const isFirefox = window.navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
      const file = await this.taskDownloadService.downloadFile(fileUrl,
        {
          open: openFile, // (!isFirefox || isIos) && !isPreview,  // (!isFirefox || isPreview || isIos),
          preventBrowserDownload: true // (isPreview || isIos)
        });

      return file;

    } catch (er) {
      this.logger.error(er, '', 'MmFaultsReportingService');
      this.notificationService.error(this.translateService.instant('MM.reporting.reportDownloadError'));
    }
  }


  openMessageCenter() {
    this.appRightDrawerService.openContent('task-dashboard', this.appContainerOptions);
  }

  public isIOSDevice(): boolean {
    return !!navigator.userAgent && /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
}
